/**
 * Getting started: the mutations behind "add a client" and "add a portfolio".
 *
 * A new advisor logs in to an empty book, so every empty state needs a real
 * next step rather than a shrug. These are the flows the backend actually
 * supports today:
 *
 *   createUser                          a client record
 *   createProspect                      a lead, before they are a client
 *   createPortfolioSnapshot             an empty portfolio to fill later
 *   createExternalPortfolioFromHoldings a portfolio built from holdings, which
 *                                       is also how a statement's contents get
 *                                       in without a CAS parser
 *
 * Creating for a client rather than for yourself is the `X-Target-User-Id`
 * header — see api/clientScope.js.
 */
import { gql } from '@apollo/client';

export const CREATE_CLIENT = gql`
  mutation CreateClient($userData: UserProfileCreateInput!) {
    createUser(userData: $userData) {
      id
      name
      email
      phoneNumber
      isActive
      kycVerified
      createdAt
      systemGeneratedId
    }
  }
`;

export const CREATE_PROSPECT = gql`
  mutation CreateProspect($input: CreateProspectInput!) {
    createProspect(input: $input) {
      success
      message
      prospect { id name email phoneNumber status source createdAt }
    }
  }
`;

export const CREATE_PORTFOLIO = gql`
  mutation CreatePortfolio($name: String!, $portfolioType: String!, $currency: String!) {
    createPortfolioSnapshot(name: $name, portfolioType: $portfolioType, currency: $currency) {
      success
      message
      portfolio {
        securitySymbol
        portfolioName
        portfolioType
        userId
        isActive
      }
    }
  }
`;

export const CREATE_PORTFOLIO_FROM_HOLDINGS = gql`
  mutation CreatePortfolioFromHoldings(
    $name: String!
    $holdings: [ExternalHoldingInput!]!
    $portfolioType: String
    $currency: String
    $computeReturns: Boolean
  ) {
    createExternalPortfolioFromHoldings(
      name: $name
      holdings: $holdings
      portfolioType: $portfolioType
      currency: $currency
      computeReturns: $computeReturns
    ) {
      success
      message
      securitySymbol
      portfolioName
      startDate
      holdingCount
      returnsComputed
      metricsComputed
    }
  }
`;
